import React from "react";
import type { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { useRouter } from "next/router";
import { Inter } from "next/font/google";
import {
  MantineProvider,
  createTheme,
  defaultCssVariablesResolver,
  type CSSVariablesResolver,
  useComputedColorScheme,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "../styles/globals.css";
import "../styles/home.css";
import { darkTheme, lightTheme } from "../constants/theme";
import { installMarketingLinkTracking, trackMarketingPageView } from "../utils/analytics";

const inter = Inter({
  subsets: ["latin"],
  weight: "variable",
  style: "normal",
  display: "swap",
});

const appFontFamily = `${inter.style.fontFamily},system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif`;

const theme = createTheme({
  fontFamily: appFontFamily,
  headings: {
    fontFamily: appFontFamily,
  },
  defaultRadius: "sm",
  radius: {
    xs: "4px",
    sm: "4px",
    md: "4px",
    lg: "4px",
    xl: "4px",
  },
});

const cssVariablesResolver: CSSVariablesResolver = (mantineTheme) => {
  const base = defaultCssVariablesResolver(mantineTheme);

  return {
    ...base,
    light: {
      ...base.light,
      "--mantine-color-body": lightTheme.BACKGROUND_PRIMARY,
      "--mantine-color-text": lightTheme.TEXT_NORMAL,
      "--mantine-color-dimmed": lightTheme.TEXT_MUTED,
      "--mantine-color-default-border": lightTheme.BACKGROUND_MODIFIER_ACCENT,
      "--mantine-color-anchor": lightTheme.ORANGE,
      "--mantine-color-default": lightTheme.BACKGROUND_PRIMARY,
      "--mantine-color-default-hover": lightTheme.BACKGROUND_SECONDARY,
      "--mantine-color-default-color": lightTheme.TEXT_NORMAL,
      "--marketing-cassandra-red": "#e3212a",
      "--marketing-header-bg": "rgba(255, 255, 255, 0.96)",
      "--marketing-header-shadow": "none",
    },
    dark: {
      ...base.dark,
      "--mantine-color-body": darkTheme.BACKGROUND_PRIMARY,
      "--mantine-color-text": darkTheme.TEXT_NORMAL,
      "--mantine-color-dimmed": darkTheme.TEXT_MUTED,
      "--mantine-color-default-border": darkTheme.BACKGROUND_MODIFIER_ACCENT,
      "--mantine-color-anchor": darkTheme.ORANGE,
      "--mantine-color-default": darkTheme.BACKGROUND_SECONDARY,
      "--mantine-color-default-hover": darkTheme.BACKGROUND_SECONDARY_ALT,
      "--mantine-color-default-color": darkTheme.TEXT_NORMAL,
      "--marketing-cassandra-red": "#e3212a",
      "--marketing-header-bg": "rgba(255, 255, 255, 0.96)",
      "--marketing-header-shadow": "none",
    },
  };
};

const appEnv = process.env.NEXT_PUBLIC_APP_ENV?.trim().toLowerCase();
const gaMeasurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();
const shouldEnableGa = appEnv === "prod" && Boolean(gaMeasurementId);

function BrowserChromeThemeSync() {
  const computedColorScheme = useComputedColorScheme("light");
  const browserThemeColor = computedColorScheme === "dark" ? darkTheme.BACKGROUND_PRIMARY : lightTheme.BACKGROUND_PRIMARY;

  return (
    <Head>
      <meta name="theme-color" content={browserThemeColor} />
    </Head>
  );
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const hasTrackedInitialPageViewRef = React.useRef(false);

  React.useEffect(() => installMarketingLinkTracking(), []);

  React.useEffect(() => {
    const handleRouteChange = (url: string) => trackMarketingPageView(url);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events]);

  React.useEffect(() => {
    if (!shouldEnableGa || !router.isReady || hasTrackedInitialPageViewRef.current) return;
    hasTrackedInitialPageViewRef.current = true;
    trackMarketingPageView(router.asPath);
  }, [router.asPath, router.isReady]);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      {shouldEnableGa ? (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
            `}
          </Script>
        </>
      ) : null}
      <MantineProvider theme={theme} defaultColorScheme="light" forceColorScheme="light" cssVariablesResolver={cssVariablesResolver}>
        <BrowserChromeThemeSync />
        <Component {...pageProps} />
      </MantineProvider>
    </>
  );
}
